import { useState } from 'react';
import Typography from '@/components/atoms/typography/typography';
import Button from '@/components/molecules/button/button';
import ExtraCard from '@/components/organisms/extra-card/extra-card';
import FilterTagList, {
  FilterItem,
} from '@/components/organisms/filter-tag-list/filter-tag-list';
import Column from '@/components/utilities/column/column';
import Container from '@/components/utilities/container/container';
import ModuleWrapper, {
  ModuleWrapperProps,
} from '@/components/utilities/module-wrapper/module-wrapper';
import Row from '@/components/utilities/row/row';
import { useI18n } from '@/libraries/contexts/i18n-context';
import styles from './extras.module.scss';

const ITEM_SHOW_THRESHOLD = 6;

export interface ExtrasProps extends ModuleWrapperProps {
  /** Items to show as cards in the grid */
  extraModuleItems: Content.Components['ExtraModuleDto']['extraModuleItems'];
  /** The filter list of all filters for the extras */
  filters: Content.Components['ExtraModuleDto']['filters'];
  /** The headline of the module */
  headline: Content.Components['ExtraModuleDto']['headline'];
  /** The item text of the module */
  itemText: Content.Components['ExtraModuleDto']['itemText'];
  /** The pre headline of the module */
  preHeadline: Content.Components['ExtraModuleDto']['preHeadline'];
}

const Extras = ({
  extraModuleItems = [],
  filters = [],
  headline,
  itemText,
  preHeadline,
  ...rest
}: ExtrasProps) => {
  const { t } = useI18n();
  const [showAll, setShowAll] = useState(false);

  const [transformedFilters, setTransformedFilters] = useState<FilterItem[]>(
    filters.map(filter => ({
      id: filter.filterId as string,
      isSelected: false,
      text: filter.filter as string,
    })),
  );

  const updateFilters = (filterId: string) => {
    setTransformedFilters(previousItems =>
      previousItems.map(item =>
        item.id === filterId
          ? { ...item, isSelected: !item.isSelected }
          : { ...item },
      ),
    );
  };

  const selectedFilterIds = transformedFilters
    .filter(filter => filter.isSelected)
    .map(filter => filter.id);

  const filteredItems = selectedFilterIds.length
    ? extraModuleItems.filter(item =>
        item.filters.some(tag =>
          selectedFilterIds.includes(tag.filterId as string),
        ),
      )
    : extraModuleItems;

  const visibleItems = showAll
    ? filteredItems
    : filteredItems.slice(0, ITEM_SHOW_THRESHOLD);

  const hiddenCount = filteredItems.length - ITEM_SHOW_THRESHOLD;

  const handleShowMore = () => setShowAll(previousValue => !previousValue);

  return (
    <ModuleWrapper {...rest} className={styles.component}>
      <Container>
        <Row className={styles.headingRowWrapper}>
          <Column width={{ desktop: 6, mobile: 12, tablet: 8 }}>
            <Typography
              className={styles.preHeadline}
              tag="label"
              tagStyle="subHeading"
            >
              {preHeadline}
            </Typography>

            <Typography
              className={styles.headline}
              tag="h2"
              tagStyle="displayMedium"
            >
              {headline}
            </Typography>
          </Column>
        </Row>

        <div className={styles.filterRowWrapper}>
          <Typography tag="label" tagStyle="headingSmall">
            {extraModuleItems.length} {itemText}
          </Typography>

          {transformedFilters.length > 0 && (
            <div className={styles.filterWrapper}>
              <Typography tag="label" tagStyle="bodySmall">
                {t('items.common.words.filter')}
              </Typography>

              <FilterTagList
                items={transformedFilters}
                onChange={filterId => updateFilters(filterId)}
              />
            </div>
          )}
        </div>

        <div className={styles.itemsRowWrapper}>
          {visibleItems.map((item, itemIndex) => (
            <ExtraCard key={itemIndex} className={styles.extraCard} {...item} />
          ))}
        </div>

        {visibleItems.length === 0 && (
          <div className={styles.buttonRowWrapper}>
            <Typography>{t('items.common.words.noResults')}</Typography>
          </div>
        )}

        {hiddenCount > 0 && (
          <div className={styles.buttonRowWrapper}>
            <Button
              className={styles.showMoreButton}
              onClick={handleShowMore}
              text={
                showAll
                  ? t('items.common.words.showLess')
                  : `${t('items.common.words.showMore')} (${hiddenCount})`
              }
              variant="tertiary"
            />
          </div>
        )}
      </Container>
    </ModuleWrapper>
  );
};

Extras.displayName = 'Extras';

export default Extras;
